/**
 * @module app.shared
 */

import { bindable, createTypedBindable, coerceFunctions, usePropertyTypeForBindable, usePropertyTypeForObservable, coerceFunctionMap } from 'aurelia-typed-observable-plugin';
import { convertToString } from 'app/shared/lib/utilities';

export function configureObservables() {

    // don't support automatic coersion based on TypeScript type
    // it doesn't reflect enough type information to know about nullability
    usePropertyTypeForBindable(false);
    usePropertyTypeForObservable(false);

    // add 'any' bindable
    bindable.any = createTypedBindable('any');
    coerceFunctions.any = function(value: unknown): any {
        return value;
    }

    // add 'object' coerce function
    bindable.object = createTypedBindable('object');
    coerceFunctions.object = function(value: unknown): object {
        if(value instanceof Object) {
            return value;
        }
        return {};
    }

    // add 'objectNull' coerce function
    coerceFunctionMap.set(Object, 'objectNull');
    bindable.objectNull = createTypedBindable('objectNull');
    coerceFunctions.objectNull = function(value: unknown): object | null {
        if(value instanceof Object) {
            return value;
        }
        return null;
    }

    // add 'array' coerce function
    bindable.array = createTypedBindable('array');
    coerceFunctions.array = function(value: unknown): any[] {
        if(Array.isArray(value)) {
            return value;
        }
        return [];
    }

    // add 'arrayNull' coerce function
    coerceFunctionMap.set(Array, 'arrayNull');
    bindable.arrayNull = createTypedBindable('arrayNull');
    coerceFunctions.arrayNull = function(value: unknown): any[] | null {
        if(Array.isArray(value)) {
            return value;
        }
        else if(value !== undefined && value !== null) {
            return [value];
        }
        return null;
    }

    // add 'function' coerce function
    coerceFunctionMap.set(Function, 'function');
    bindable.function = createTypedBindable('function');
    coerceFunctions.function = function(value: unknown): Function | null {
        if(value instanceof Function) {
            return value;
        }
        return null;
    }

    // replace 'string' coerce function so that null doesn't become 'null' (mwj: sigh)
    coerceFunctions.string = function(value: unknown): string {
      return convertToString(value);
    }

    // add 'stringNull' coerce function
    coerceFunctionMap.set(String, 'stringNull');
    bindable.stringNull = createTypedBindable('stringNull');
    coerceFunctions.stringNull = function(value: unknown): string | null {
        return value === undefined || value === null ? null : convertToString(value);
    }

    // replace 'number' coerce function to not allow nulls
    coerceFunctions.number = function(value: unknown): number {
        if(value === undefined || value === null) { return 0; }
        if(typeof value === 'number') { return value; }
        const result = parseFloat(convertToString(value));
        if(Number.isNaN(result)) { return 0; }
        return result;
    }

    // add 'numberNull' coerce function
    coerceFunctionMap.set(Number, 'numberNull');
    bindable.numberNull = createTypedBindable('numberNull');
    coerceFunctions.numberNull = function(value: unknown): number | null {
        if(value === undefined || value === null) { return null; }
        if(typeof value === 'number') { return value; }
        const result = parseFloat(convertToString(value));
        if(Number.isNaN(result)) { return null; }
        return result;
    }

    // replace 'date' coerce function to not allow nulls
    coerceFunctions.date = function(value: unknown): Date {
        if(value === undefined || value === null) { return new Date(0); }
        let result: Date | null = null;
        if(value instanceof Date) {
            result = value;
        }
        else if(typeof value === 'number') {
            result = new Date(value);
        }
        else {
            const stringValue = convertToString(value);
            result = new Date(stringValue);
        }
        if(isNaN(result.getTime())) { return new Date(0); }
        return result;
    }

    // add 'dateNull' coerce function
    coerceFunctionMap.set(Date, 'dateNull');
    bindable.dateNull = createTypedBindable('dateNull');
    coerceFunctions.dateNull = function(value: unknown): Date | null {
        if(value === undefined || value === null) { return null; }
        let result: Date | null = null;
        if(value instanceof Date) {
            result = value;
        }
        else if(typeof value === 'number') {
            result = new Date(value);
        }
        else {
            const stringValue = convertToString(value);
            result = new Date(stringValue);
        }
        if(isNaN(result.getTime())) { return null; }
        return result;
    }

}

declare module 'aurelia-typed-observable-plugin/dist/types/bindable' {

	interface IBindableDecorator {
        any: IBindableDecorator<any>;
        object: IBindableDecorator<object>;
        objectNull: IBindableDecorator<object | null>;
        array: IBindableDecorator<any[]>;
        arrayNull: IBindableDecorator<any[] | null>;
        function: IBindableDecorator<Function | null>;
		stringNull: IBindableDecoratorFunction<string | null>;
        numberNull: IBindableDecoratorFunction<number | null>;
        dateNull: IBindableDecoratorFunction<Date | null>;
	}

}
